import {
  buildAssertionSummariesFromRunRows,
  normalizeCriterionSummaries,
} from "./evaluate-drawer-utils";

import type {
  PlaygroundEvaluateColumn,
  PlaygroundEvaluateRunRow,
} from "@/queries/playground/evaluate";
import { ScorecardPrimitiveType, type Scorecard } from "@/queries/smart-tables/scorecard";
import { SCORECARD_EVALUATOR_GRID } from "@/routes/smart-table/components/SmartTablePage/RightPanel/ScoreSidebarContent/Scorecard/constants";
import ScorecardSectionBlockHeader from "@/routes/smart-table/components/SmartTablePage/RightPanel/ScoreSidebarContent/Scorecard/ScorecardView/Detail/ScorecardSectionBlockHeader";
import ScorecardCriterionRow from "@/routes/smart-table/components/SmartTablePage/RightPanel/ScoreSidebarContent/Scorecard/ScorecardView/Overview/ScorecardCriterionRow";

interface CompactEvaluatorCardsProps {
  runSummary: Record<string, unknown> | null | undefined;
  scorecardSnapshot: unknown;
  columns: PlaygroundEvaluateColumn[];
  rows: PlaygroundEvaluateRunRow[];
  staleStepIds: string[];
  editableStepIds: string[];
  onOpenCriterion: (stepId: string) => void;
  onEditCriterion: (stepId: string) => void;
}

const CompactEvaluatorCards = ({
  runSummary,
  scorecardSnapshot,
  columns,
  rows,
  staleStepIds,
  editableStepIds,
  onOpenCriterion,
  onEditCriterion,
}: CompactEvaluatorCardsProps) => {
  const scorecard =
    scorecardSnapshot && typeof scorecardSnapshot === "object"
      ? (scorecardSnapshot as Scorecard)
      : null;
  const steps = scorecard?.steps ?? [];
  const summaries = normalizeCriterionSummaries(runSummary?.criterion_summaries);
  const assertionSummariesByStep = new Map(
    steps
      .filter(step => step.primitive_type === ScorecardPrimitiveType.LLM_ASSERTION)
      .map(step => [step.id, buildAssertionSummariesFromRunRows(rows, step)] as const),
  );

  if (steps.length === 0) {
    return null;
  }

  return (
    <section className="shrink-0 space-y-2">
      <ScorecardSectionBlockHeader
        label={`Evaluators · ${steps.length}`}
        description="See how each evaluator scored this Playground run."
      />
      <div className={SCORECARD_EVALUATOR_GRID}>
        {steps.map(step => (
          <ScorecardCriterionRow
            key={step.id}
            step={step}
            summary={summaries.find(summary => summary.step_id === step.id)}
            columns={columns}
            assertionSummaries={assertionSummariesByStep.get(step.id)}
            isStale={staleStepIds.includes(step.id)}
            onOpen={() => onOpenCriterion(step.id)}
            onEdit={editableStepIds.includes(step.id) ? () => onEditCriterion(step.id) : undefined}
            displayMode="card"
          />
        ))}
      </div>
    </section>
  );
};

export default CompactEvaluatorCards;
